import React, { useEffect, useMemo, useState } from 'react';
import { Globe2, History, Landmark, Layers, Search, Sparkles, UserCheck } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  SegmentedControl,
  Select,
} from '../../../components/ui';
import { fetchCountryOptions } from '../governmentApi';
import type { AutoCheck, CountryInput, CountryProfile, StartRunInput } from '../types';

import { foldText } from '../../../utils/fuzzySearch';
/* ------------------------------------------------------------------ */
/* Country                                                             */
/* ------------------------------------------------------------------ */

const LANGUAGE_NAME = (code: string): string => {
  try {
    return new Intl.DisplayNames(undefined, { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
};

/**
 * Which country this directory is about — the setting everything else here
 * hangs off, so it says plainly what changing it means.
 */
export const CountryDialog: React.FC<{
  open: boolean;
  country: CountryProfile;
  busy?: boolean;
  onClose: () => void;
  onSave: (input: CountryInput) => Promise<unknown>;
}> = ({ open, country, busy, onClose, onSave }) => {
  const [options, setOptions] = useState<Array<{ code: string; name: string; languages: string[] }>>([]);
  const [code, setCode] = useState(country.code);
  const [languages, setLanguages] = useState(country.languages.join(', '));
  const [nameLanguage, setNameLanguage] = useState(country.nameLanguage);
  const [autoCheck, setAutoCheck] = useState<AutoCheck>(country.autoCheck);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setCode(country.code);
    setLanguages(country.languages.join(', '));
    setNameLanguage(country.nameLanguage);
    setAutoCheck(country.autoCheck);
    setSearch('');
    void fetchCountryOptions()
      .then(setOptions)
      .catch(() => setOptions([]));
  }, [open, country]);

  const chosen = options.find((option) => option.code === code);
  const list = useMemo(() => {
    const term = foldText(search);
    const all = options.length ? options : [{ code: country.code, name: country.name, languages: country.languages }];
    return term ? all.filter((option) => foldText(option.name).includes(term)) : all;
  }, [options, search, country]);

  const languageList = languages
    .split(/[,\s]+/)
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

  const changing = code !== country.code;

  return (
    <Dialog open={open} onClose={onClose} label="Country" handle className="sm:max-w-md">
      <DialogHeader eyebrow="What this directory is about" title="Country" onClose={onClose} />
      <DialogBody className="space-y-4">
        <Field label="Country" htmlFor="country-search">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" strokeWidth={1.75} />
            <Input
              id="country-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={chosen?.name ?? country.name}
              className="pl-9"
            />
          </div>
        </Field>
        <div className="max-h-56 overflow-y-auto rounded-xl border border-neutral-200">
          <ul className="divide-y divide-neutral-100">
            {list.slice(0, 120).map((option) => (
              <li key={option.code}>
                <button
                  type="button"
                  onClick={() => {
                    setCode(option.code);
                    setLanguages(option.languages.join(', '));
                    setNameLanguage(option.languages[0]);
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors ${
                    option.code === code ? 'bg-primary-50 text-primary-700' : 'hover:bg-neutral-50'
                  }`}
                >
                  <span className="w-7 flex-shrink-0 font-ui-mono text-[11px] uppercase text-neutral-400">
                    {option.code}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{option.name}</span>
                  <span className="flex-shrink-0 text-[11.5px] text-neutral-400">
                    {option.languages.map(LANGUAGE_NAME).join(', ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <Field
          label="Research languages"
          htmlFor="country-languages"
          hint="Codes, most official first — searches are written in these"
        >
          <Input
            id="country-languages"
            value={languages}
            onChange={(event) => setLanguages(event.target.value)}
            placeholder="fr, en"
          />
        </Field>

        <Field label="Names are written in" htmlFor="country-name-language">
          <Select
            id="country-name-language"
            value={nameLanguage}
            onChange={(event) => setNameLanguage(event.target.value)}
          >
            {(languageList.length ? languageList : [country.nameLanguage]).map((language) => (
              <option key={language} value={language}>
                {LANGUAGE_NAME(language)}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Keep the map current on its own"
          htmlFor="country-auto"
          hint={
            autoCheck === 'off'
              ? undefined
              : `Appointments ${autoCheck === 'daily' ? 'every day' : 'every week'}; renamed, split and merged ministries every week. Changes wait for review.`
          }
        >

          <SegmentedControl
            label="Automatic checks"
            items={[
              { value: 'off', label: 'Off' },
              { value: 'weekly', label: 'Weekly' },
              { value: 'daily', label: 'Daily' },
            ]}
            value={autoCheck}
            onChange={(value) => setAutoCheck(value as AutoCheck)}
          />
        </Field>

        {changing && (
          <p className="rounded-xl bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
            The structure already mapped for {country.name} is kept, but the page will show{' '}
            {chosen?.name ?? code} from now on.
          </p>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={busy}
          onClick={async () => {
            await onSave({ code, languages: languageList, nameLanguage, autoCheck });
            onClose();
          }}
        >
          Save
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/* Research                                                            */
/* ------------------------------------------------------------------ */

type Choice = 'structure-full' | 'structure-core' | 'officials' | 'appointments' | 'timeline' | 'curate';

const CHOICES: Array<{
  value: Choice;
  icon: typeof Landmark;
  title: string;
  /** One line on the tile */
  hint: string;
  /** Shown for the one chosen */
  detail: string;
  cost: string;
}> = [
  {
    value: 'structure-full',
    icon: Landmark,
    title: 'Map the whole state',
    hint: 'Every body, down to agencies',
    detail:
      'The institutions and every ministry, then what sits inside each of them — directorates, agencies and state companies.',
    cost: 'Tens of searches, several minutes',
  },
  {
    value: 'structure-core',
    icon: Globe2,
    title: 'Institutions and ministries',
    hint: 'The top level only',
    detail: 'The presidency, the government and its ministries, the courts and the independent institutions.',
    cost: 'One pass, about a minute',
  },
  {
    value: 'officials',
    icon: UserCheck,
    title: 'Who holds office',
    hint: 'The heads of the bodies on file',
    detail:
      'For each entity in the directory: who heads it today, and whether the people recorded there are still in post.',
    cost: 'One pass per five entities',
  },
  {
    value: 'appointments',
    icon: Sparkles,
    title: 'Recent appointments',
    hint: 'Decrees over a period',
    detail: 'Decrees and announcements over a period, matched to the people and bodies you already hold.',
    cost: 'One pass',
  },
  {
    value: 'timeline',
    icon: History,
    title: 'Who held each post',
    hint: 'And since when, act by act',
    detail:
      'Reads the decrees that formed and reshaped the government, and those naming the secretaries general, one act at a time. Each holder is dated from the act that first appointed them, not the latest one that kept them; whoever the acts show has left is proposed to leave.',
    cost: 'A pass per year of acts, then one per act — up to 24 a run; run it again to read on',
  },
  {
    value: 'curate',
    icon: Layers,
    title: 'Find duplicates',
    hint: 'Bodies held twice, posts as bodies',
    detail:
      'Reads every entry of the map and its record for bodies held twice, posts listed as bodies and records tied to the wrong body. Finds no new bodies; doubtful cases are checked against the official acts, and what holds up is proposed under Updates.',
    cost: 'A few model calls, plus a web check for the doubtful ones',
  },
];

/** What to research, said in terms of what comes back rather than how it works */
export const ResearchDialog: React.FC<{
  open: boolean;
  countryName: string;
  researchReady: boolean;
  busy?: boolean;
  onClose: () => void;
  onStart: (input: StartRunInput) => void;
  /** Something is already running, so this will wait its turn */
  queueing?: boolean;
}> = ({ open, countryName, researchReady, busy, onClose, onStart, queueing = false }) => {
  const [choice, setChoice] = useState<Choice>('structure-full');
  const [windowDays, setWindowDays] = useState('60');
  const [years, setYears] = useState('5');
  const [focus, setFocus] = useState('');

  useEffect(() => {
    if (open) {
      setChoice('structure-full');
      setFocus('');
    }
  }, [open]);

  const chosen = CHOICES.find((item) => item.value === choice) ?? CHOICES[0];

  const start = () => {
    const focusText = focus.trim() || null;
    if (choice === 'structure-full') onStart({ kind: 'structure', depth: 'full', focus: focusText });
    else if (choice === 'structure-core') onStart({ kind: 'structure', depth: 'core', focus: focusText });
    else if (choice === 'officials') onStart({ kind: 'officials', focus: focusText });
    else if (choice === 'curate') onStart({ kind: 'curate' });
    else if (choice === 'timeline') {
      const since = new Date();
      since.setFullYear(since.getFullYear() - Number(years));
      onStart({ kind: 'timeline', since: since.toISOString().slice(0, 10), focus: focusText });
    }
    else onStart({ kind: 'appointments', windowDays: Number(windowDays), focus: focusText });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} label="Research" handle className="sm:max-w-xl">
      <DialogHeader eyebrow={countryName} title="Research" onClose={onClose} />
      <DialogBody className="space-y-3">
        {queueing && researchReady && (
          <p className="rounded-xl bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-700">
            Research is already running. This will wait its turn and start on its own.
          </p>
        )}
        {!researchReady && (
          <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[12.5px] text-primary-700">
            No AI provider that can search the web is set up. Research would only repeat what a model
            remembers, so it is turned off until a key is added in AI Settings.
          </p>
        )}
        {/* Six small tiles; what the chosen one does is told once, below them */}
        <div role="radiogroup" aria-label="What to research" className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {CHOICES.map((item) => {
            const Icon = item.icon;
            const active = choice === item.value;
            return (
              <button
                key={item.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setChoice(item.value)}
                className={`flex min-w-0 items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors ${
                  active ? 'border-primary-600 bg-primary-50/50' : 'border-neutral-200 hover:bg-neutral-50'
                }`}
              >
                <Icon
                  className={`h-4 w-4 flex-shrink-0 ${active ? 'text-primary-600' : 'text-neutral-400'}`}
                  strokeWidth={1.75}
                />
                <span className="min-w-0">
                  <span className="block truncate text-[13px] text-ink">{item.title}</span>
                  <span className="block truncate text-[11.5px] text-neutral-500">{item.hint}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="rounded-xl bg-neutral-50 px-3.5 py-2.5">
          <p className="text-[12px] leading-snug text-neutral-600">{chosen.detail}</p>
          <p className="mt-1 text-[11.5px] text-neutral-400">{chosen.cost}</p>
        </div>

        {choice !== 'curate' && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            {choice === 'appointments' && (
              <Field label="How far back" htmlFor="window-days" className="flex-shrink-0">
                <SegmentedControl
                  label="Period"
                  items={[
                    { value: '30', label: '30 d' },
                    { value: '60', label: '60 d' },
                    { value: '90', label: '90 d' },
                    { value: '180', label: '6 mo' },
                  ]}
                  value={windowDays}
                  onChange={setWindowDays}
                />
              </Field>
            )}
            {choice === 'timeline' && (
              <Field label="How far back" htmlFor="timeline-years" className="flex-shrink-0">
                <SegmentedControl
                  label="Period"
                  items={[
                    { value: '2', label: '2 y' },
                    { value: '5', label: '5 y' },
                    { value: '10', label: '10 y' },
                  ]}
                  value={years}
                  onChange={setYears}
                />
              </Field>
            )}
            <Field
              label={
                <>
                  Concentrate on <span className="text-neutral-400">· optional</span>
                </>
              }
              htmlFor="research-focus"
              className="min-w-0 flex-1"
            >
              <Input
                id="research-focus"
                value={focus}
                onChange={(event) => setFocus(event.target.value)}
                placeholder="e.g. mining and energy bodies"
              />
            </Field>
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={start} loading={busy} disabled={!researchReady}>
          {queueing ? 'Add to queue' : 'Start'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
